import React, { useMemo } from "react"
import {
  Table,
  Space,
  Typography,
  Button,
  Tag,
  Dropdown,
  message,
} from "@mimber/ui-web"
import type { ColumnsType } from "@mimber/ui-web"
import {
  TeamOutlined,
  ClockCircleOutlined,
  MoreOutlined,
  DeleteOutlined,
} from "@mimber/ui-web"
import { useGroupRoleAssignments } from "@mimber/client/mimber-organization"
import { useRevokeGroupRoleAssignmentModal } from "@/components/modals"
import type { GroupRoleAssignmentWithRelationsDto } from "@mimber/schema"
import { formatTableDate } from "@mimber/ui-web"
import type { GroupRoleAssignmentsTableProps } from "./GroupRoleAssignmentsTable.types"

const { Text } = Typography

export const GroupRoleAssignmentsTable: React.FunctionComponent<
  GroupRoleAssignmentsTableProps
> = ({ roleUuid, onRowClick, className }) => {
  const { data: response, isLoading, error } = useGroupRoleAssignments(roleUuid)

  React.useEffect(() => {
    if (error) {
      message.error(`Failed to load group assignments: ${error.message}`)
    }
  }, [error])

  const { contextHolder, showRevokeGroupRoleAssignmentModal } =
    useRevokeGroupRoleAssignmentModal(roleUuid)

  const handleRevoke = (record: GroupRoleAssignmentWithRelationsDto) => {
    showRevokeGroupRoleAssignmentModal({ assignment: record })
  }

  const columns: ColumnsType<GroupRoleAssignmentWithRelationsDto> = useMemo(
    () => [
      {
        title: "Group",
        key: "group",
        render: (_, record) => (
          <Space>
            <TeamOutlined />
            <Text strong>{record.group.name}</Text>
          </Space>
        ),
      },
      {
        title: "Expires",
        key: "expiresAt",
        render: (_, record) => {
          if (!record.expiresAt) return <Text type="secondary">Never</Text>
          const isExpired = new Date(record.expiresAt) < new Date()
          return (
            <Space>
              <ClockCircleOutlined />
              <Text type={isExpired ? "danger" : undefined}>
                {formatTableDate(record.expiresAt)}
              </Text>
              {isExpired && <Tag color="red">Expired</Tag>}
            </Space>
          )
        },
      },
      {
        title: "Assigned",
        key: "createdAt",
        render: (_, record) => (
          <Space>
            <ClockCircleOutlined />
            <Text type="secondary">{formatTableDate(record.createdAt)}</Text>
          </Space>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        fixed: "right",
        width: 80,
        render: (_, record) => (
          <div onClick={(e) => e.stopPropagation()}>
            <Dropdown
              menu={{
                items: [
                  {
                    key: "revoke",
                    label: "Revoke",
                    icon: <DeleteOutlined />,
                    danger: true,
                    onClick: () => handleRevoke(record),
                  },
                ],
              }}
              trigger={["click"]}
            >
              <Button type="text" icon={<MoreOutlined />} />
            </Dropdown>
          </div>
        ),
      },
    ],
    [handleRevoke]
  )

  return (
    <>
      {contextHolder}
      <Table<GroupRoleAssignmentWithRelationsDto>
        bordered
        columns={columns}
        dataSource={response?.data ?? []}
        loading={isLoading}
        rowKey="uuid"
        pagination={false}
        className={className}
        locale={{ emptyText: "No groups assigned" }}
        onRow={(record) => ({
          onClick: () => onRowClick?.(record),
          style: { cursor: onRowClick ? "pointer" : "default" },
        })}
      />
    </>
  )
}
